// src/pages/SolutionPage.js
// Template for the individual solution pages (/solutions/:slug).
// All content comes from the locales under solutions.<key>.
import React from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageMeta from '../components/SEO/PageMeta';
import StructuredData from '../components/SEO/StructuredData';
import CtaBanner from '../components/CtaBanner';
import { getSolutionBySlug, SOLUTIONS } from '../data/solutions';

const ListCard = ({ title, items, columns = 1 }) => (
    <div className="bg-white rounded-xl shadow-lg p-6" data-aos="fade-up">
        <h2 className="text-xl font-bold text-primary mb-4">{title}</h2>
        <ul className={`gap-x-6 gap-y-2 grid ${columns === 2 ? 'sm:grid-cols-2' : 'grid-cols-1'}`}>
            {Array.isArray(items) && items.map((item, i) => (
                <li key={i} className="flex items-start text-gray-700">
                    <svg className="w-5 h-5 text-accent flex-shrink-0 mt-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    <span className="ml-2">{item}</span>
                </li>
            ))}
        </ul>
    </div>
);

const SolutionPage = () => {
    const { slug } = useParams();
    const { t } = useTranslation();
    const solution = getSolutionBySlug(slug);

    if (!solution) {
        return <Navigate to="/solutions" replace />;
    }

    const k = `solutions.${solution.key}`;
    const how = t(`${k}.how`, { returnObjects: true }) || [];
    const sources = t(`${k}.sources`, { returnObjects: true }) || [];
    const value = t(`${k}.value`, { returnObjects: true }) || [];
    const integration = t(`${k}.integration`, { returnObjects: true }) || [];
    const architecture = t(`${k}.architecture`, { returnObjects: true }) || [];
    const useCases = t(`${k}.use_cases`, { returnObjects: true }) || [];
    // Optional deep-dive sections (only some solutions define them)
    const extraSectionsRaw = t(`${k}.extra_sections`, { returnObjects: true, defaultValue: [] });
    const extraSections = Array.isArray(extraSectionsRaw) ? extraSectionsRaw : [];
    const otherSolutions = SOLUTIONS.filter((s) => s.slug !== slug).slice(0, 3);

    return (
        <>
            <PageMeta
                title={`${t(`${k}.title`)} | ROBOMIND Systems`}
                description={t(`${k}.meta_description`)}
                path={`/solutions/${slug}`}
            />

            <StructuredData
                type="solution"
                solution={{
                    name: t(`${k}.title`),
                    description: t(`${k}.meta_description`),
                    slug,
                }}
            />

            {/* Page header */}
            <div className="bg-slate-900 py-16">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-aos="fade-up">
                    <div className="text-accent mb-4">{solution.icon}</div>
                    <h1 className="text-4xl font-bold text-white mb-4">{t(`${k}.title`)}</h1>
                    <p className="text-xl text-gray-300">{t(`${k}.tagline`)}</p>
                </div>
            </div>

            <div className="bg-gray-100 py-12">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">

                    {/* The industrial problem */}
                    <div className="bg-white rounded-xl shadow-lg p-6" data-aos="fade-up">
                        <h2 className="text-xl font-bold text-primary mb-4">{t('solutions_common.problem_title')}</h2>
                        <p className="text-gray-700 text-justify">{t(`${k}.problem`)}</p>
                    </div>

                    {/* How it works */}
                    <div className="bg-white rounded-xl shadow-lg p-6" data-aos="fade-up">
                        <h2 className="text-xl font-bold text-primary mb-4">{t('solutions_common.how_title')}</h2>
                        {Array.isArray(how) && how.map((p, i) => (
                            <p key={i} className={`text-gray-700 text-justify ${i < how.length - 1 ? 'mb-4' : ''}`}>{p}</p>
                        ))}
                    </div>

                    {/* Optional deep-dive sections */}
                    {extraSections.map((section, i) => (
                        <ListCard key={i} title={section.title} items={section.items} columns={2} />
                    ))}

                    {/* Data sources + business value side by side */}
                    <div className="grid md:grid-cols-2 gap-8">
                        <ListCard title={t('solutions_common.sources_title')} items={sources} />
                        <ListCard title={t('solutions_common.value_title')} items={value} />
                    </div>

                    {/* Deployment architecture as numbered steps */}
                    <div className="bg-slate-900 rounded-xl shadow-lg p-6" data-aos="fade-up">
                        <h2 className="text-xl font-bold text-white mb-6">{t('solutions_common.architecture_title')}</h2>
                        <ol className="space-y-4">
                            {Array.isArray(architecture) && architecture.map((step, i) => (
                                <li key={i} className="flex items-start">
                                    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-white font-bold flex items-center justify-center text-sm">{i + 1}</span>
                                    <span className="ml-4 text-gray-200 pt-1">{step}</span>
                                </li>
                            ))}
                        </ol>
                    </div>

                    {/* Integration + use cases side by side */}
                    <div className="grid md:grid-cols-2 gap-8">
                        <ListCard title={t('solutions_common.integration_title')} items={integration} />
                        <ListCard title={t('solutions_common.use_cases_title')} items={useCases} />
                    </div>

                    {/* Related solutions */}
                    <div data-aos="fade-up">
                        <h2 className="text-xl font-bold text-gray-800 mb-4">{t('common.all_solutions')}</h2>
                        <div className="grid sm:grid-cols-3 gap-4">
                            {otherSolutions.map((s) => (
                                <Link
                                    key={s.slug}
                                    to={`/solutions/${s.slug}`}
                                    className="bg-white group rounded-lg shadow p-4 flex items-center hover:shadow-lg transition-shadow"
                                >
                                    <div className="text-primary mr-3 scale-75">{s.icon}</div>
                                    <span className="text-sm font-semibold text-gray-700 group-hover:text-primary transition-colors">
                                        {t(`solutions.${s.key}.title`)}
                                    </span>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <CtaBanner
                title={t('solutions_common.cta_title')}
                text={t('solutions_common.cta_text')}
                buttonLabel={t('solutions_common.cta_button')}
            />
        </>
    );
};

export default SolutionPage;
